import { useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Plus,
  Trash2,
  Building2,
  UtensilsCrossed,
  Car,
  Phone,
  Receipt,
  Upload,
  Loader2,
  Sparkles,
  Check,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import type { Expense, ExpenseType, Currency } from '../types';
import { expenseTypes, currencies, type ClaimTheme } from '../constants';
import { AnimatedButton } from '../shared/AnimatedButton';
import { cn } from '@/lib/utils';
import api from '@/utils/api';

interface ExpensesSectionProps {
  expenses: Expense[];
  onAddExpense: (expense: Omit<Expense, 'id' | '_isNew'>) => void;
  onRemoveExpense: (id: string) => void;
  onContinue: () => void;
  onSkip: () => void;
  onBack: () => void;
  caseId: string | null;
  theme?: ClaimTheme;
}

interface ExtractedExpense {
  expense_type: ExpenseType;
  amount_cents: number;
  currency: Currency;
  description?: string;
  merchant_name?: string;
  date?: string;
  confidence: number;
  document_id: number;
  page_number?: number;
}

const expenseIcons: Record<ExpenseType, typeof Receipt> = {
  hotel: Building2,
  meal: UtensilsCrossed,
  transport: Car,
  phone: Phone,
  other: Receipt,
};

export function ExpensesSection({
  expenses,
  onAddExpense,
  onRemoveExpense,
  onContinue,
  onSkip,
  onBack,
  caseId,
  theme = 'invol',
}: ExpensesSectionProps) {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    expense_type: 'meal' as ExpenseType,
    amount: '',
    currency: 'EUR' as Currency,
    description: '',
  });

  // Receipt upload state
  const [isUploading, setIsUploading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedExpense, setExtractedExpense] = useState<ExtractedExpense | null>(null);
  const [extractedExpenses, setExtractedExpenses] = useState<ExtractedExpense[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle receipt upload and extraction
  const handleReceiptUpload = useCallback(
    async (file: File) => {
      if (!caseId) {
        toast.error('Please complete the booking step first');
        return;
      }

      setIsUploading(true);
      setIsExtracting(false);

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('kind', 'expense_receipt');

        const uploadResponse = await api.post(
          `/api/documents/cases/${caseId}/documents/`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        const docId = uploadResponse.data.document?.id;
        if (!docId) {
          throw new Error('Document upload failed');
        }

        setIsUploading(false);
        setIsExtracting(true);

        const extractResponse = await api.post(`/api/documents/${docId}/extract-expense/`);

        if (extractResponse.data.status === 'success') {
          const expensesData = extractResponse.data.expenses;

          if (Array.isArray(expensesData)) {
            const parsed: ExtractedExpense[] = expensesData.map((exp: any) => ({
              expense_type: exp.expense_type,
              amount_cents: exp.amount_cents,
              currency: exp.currency,
              description: exp.description || '',
              merchant_name: exp.merchant_name,
              date: exp.date,
              confidence: exp.confidence,
              document_id: docId,
              page_number: exp.page_number,
            }));

            if (parsed.length === 0) {
              toast.warning('No expenses found in this document');
            } else if (parsed.length === 1) {
              setExtractedExpense(parsed[0]);
              toast.success('Receipt analyzed! Review the details below.');
            } else {
              setExtractedExpenses(parsed);
              toast.success(`Found ${parsed.length} expenses! Review below.`);
            }
          } else {
            setExtractedExpense({
              expense_type: extractResponse.data.expense_type,
              amount_cents: extractResponse.data.amount_cents,
              currency: extractResponse.data.currency,
              description: extractResponse.data.description || '',
              merchant_name: extractResponse.data.merchant_name,
              date: extractResponse.data.date,
              confidence: extractResponse.data.confidence,
              document_id: docId,
            });
            toast.success('Receipt analyzed! Review the details below.');
          }
        } else {
          throw new Error(extractResponse.data.message || 'Extraction failed');
        }
      } catch (err: any) {
        console.error('Receipt upload/extraction failed:', err);
        toast.error(err.message || 'Failed to process receipt');
      } finally {
        setIsUploading(false);
        setIsExtracting(false);
      }
    },
    [caseId]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleReceiptUpload(file);
      }
      e.target.value = '';
    },
    [handleReceiptUpload]
  );

  const handleConfirmExtraction = useCallback(() => {
    if (!extractedExpense) return;
    onAddExpense({
      expense_type: extractedExpense.expense_type,
      amount_cents: extractedExpense.amount_cents,
      currency: extractedExpense.currency,
      description: extractedExpense.description || '',
      merchant_name: extractedExpense.merchant_name,
      date: extractedExpense.date,
    });
    setExtractedExpense(null);
  }, [extractedExpense, onAddExpense]);

  const handleCancelExtraction = useCallback(() => {
    setExtractedExpense(null);
  }, []);

  const handleConfirmAllExtracted = useCallback(() => {
    extractedExpenses.forEach((exp) => {
      onAddExpense({
        expense_type: exp.expense_type,
        amount_cents: exp.amount_cents,
        currency: exp.currency,
        description: exp.description || '',
        merchant_name: exp.merchant_name,
        date: exp.date,
      });
    });
    setExtractedExpenses([]);
  }, [extractedExpenses, onAddExpense]);

  const handleCancelAllExtracted = useCallback(() => {
    setExtractedExpenses([]);
  }, []);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        handleReceiptUpload(acceptedFiles[0]);
      }
    },
    [handleReceiptUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: 32 * 1024 * 1024,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/webp': ['.webp'],
      'image/gif': ['.gif'],
    },
    disabled: !caseId || isUploading || isExtracting,
    multiple: false,
    noClick: true,
  });

  const handleSubmit = () => {
    const amountCents = Math.round(parseFloat(formData.amount) * 100);
    if (isNaN(amountCents) || amountCents <= 0) return;

    onAddExpense({
      expense_type: formData.expense_type,
      amount_cents: amountCents,
      currency: formData.currency,
      description: formData.description,
    });

    setFormData({
      expense_type: 'meal',
      amount: '',
      currency: 'EUR',
      description: '',
    });
    setShowForm(false);
  };

  // Calculate totals grouped by currency
  const totalsByCurrency = expenses.reduce(
    (acc, exp) => {
      acc[exp.currency] = (acc[exp.currency] || 0) + exp.amount_cents;
      return acc;
    },
    {} as Record<string, number>
  );

  // Format totals: "€125.50 + $45.00" or just "€125.50" if single currency
  const formatTotals = () => {
    const entries = Object.entries(totalsByCurrency);
    return entries
      .map(([currency, cents]) => {
        const symbol = currencies.find((c) => c.value === currency)?.symbol || currency;
        return `${symbol}${(cents / 100).toFixed(2)}`;
      })
      .join(' + ');
  };

  // Helper to get currency symbol for individual expense
  const getCurrencySymbol = (currency: string) =>
    currencies.find((c) => c.value === currency)?.symbol || currency;

  return (
    <motion.div
      key="expenses"
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -30 }}
      transition={{ duration: 0.3 }}
      {...getRootProps()}
      className={cn(
        'flex flex-col relative transition-all duration-200 rounded-xl',
        isDragActive && 'ring-2 ring-amber-400 bg-amber-50/30'
      )}
    >
      <input {...getInputProps()} />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Drag overlay */}
      {isDragActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 z-10 flex items-center justify-center bg-amber-50/90 rounded-xl border-2 border-dashed border-amber-400"
        >
          <div className="text-center">
            <Receipt className="h-10 w-10 mx-auto text-amber-500 mb-2" />
            <p className="text-amber-600 font-medium">Beleg hier ablegen</p>
          </div>
        </motion.div>
      )}

      {/* Back button */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={onBack}
        className="self-start -ml-2 mb-4 p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
      </motion.button>

      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-3xl font-bold text-slate-900"
        >
          Ausgaben?
        </motion.h2>

        {expenses.length > 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-lg font-semibold"
          >
            {formatTotals()}
          </motion.div>
        )}
      </div>

      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="text-slate-500 mb-6"
      >
        Hotels, Mahlzeiten, Transportkosten...
      </motion.p>

      {/* Expense list */}
      {expenses.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-2 mb-4"
        >
          {expenses.map((expense, index) => {
            const Icon = expenseIcons[expense.expense_type];
            return (
              <motion.div
                key={expense.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-3 p-3 rounded-lg bg-white border border-slate-200"
              >
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700">
                    {expense.merchant_name || expense.expense_type}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {expense.description}
                    {expense.date && ` • ${expense.date}`}
                  </p>
                </div>
                <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">
                  {getCurrencySymbol(expense.currency)}
                  {(expense.amount_cents / 100).toFixed(2)}
                </span>
                <button
                  onClick={() => onRemoveExpense(expense.id)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* Extracted expense review */}
      {extractedExpense && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-4 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 mb-4"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <span className="text-sm font-medium text-amber-700">KI-Extraktion - Bitte überprüfen</span>
            <span className="ml-auto text-xs text-amber-500">
              {Math.round(extractedExpense.confidence * 100)}% Sicherheit
            </span>
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 w-20">Typ:</span>
              <span className="text-sm font-medium text-slate-700 capitalize">
                {extractedExpense.expense_type}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 w-20">Betrag:</span>
              <span className="text-base font-semibold text-slate-900">
                {currencies.find((c) => c.value === extractedExpense.currency)?.symbol}
                {(extractedExpense.amount_cents / 100).toFixed(2)}
              </span>
            </div>
            {extractedExpense.description && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500 w-20">Beschr.:</span>
                <span className="text-sm text-slate-700 truncate">{extractedExpense.description}</span>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={handleCancelExtraction}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700"
            >
              <X className="w-4 h-4" />
              Verwerfen
            </button>
            <AnimatedButton
              variant="primary"
              size="sm"
              onClick={handleConfirmExtraction}
              icon={<Check className="w-4 h-4" />}
              theme={theme}
            >
              Ausgabe hinzufügen
            </AnimatedButton>
          </div>
        </motion.div>
      )}

      {/* Multi-expense review */}
      {extractedExpenses.length > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-4 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 mb-4"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <span className="text-sm font-medium text-amber-700">
              {extractedExpenses.length} Ausgaben gefunden
            </span>
          </div>

          <div className="space-y-2 mb-4">
            {extractedExpenses.map((expense, index) => {
              const Icon = expenseIcons[expense.expense_type];
              return (
                <div
                  key={index}
                  className="flex items-center gap-2 p-2 rounded-lg bg-white/60 border border-amber-100"
                >
                  <Icon className="w-4 h-4 text-amber-600" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-700">
                        {expense.merchant_name || expense.expense_type}
                      </span>
                      {expense.page_number && (
                        <span className="text-xs text-slate-400">
                          Seite {expense.page_number}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">
                      {expense.description}
                      {expense.date && ` • ${expense.date}`}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">
                    {getCurrencySymbol(expense.currency)}
                    {(expense.amount_cents / 100).toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={handleCancelAllExtracted}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700"
            >
              <X className="w-4 h-4" />
              Alle verwerfen
            </button>
            <AnimatedButton
              variant="primary"
              size="sm"
              onClick={handleConfirmAllExtracted}
              icon={<Check className="w-4 h-4" />}
              theme={theme}
            >
              Alle hinzufügen ({extractedExpenses.length})
            </AnimatedButton>
          </div>
        </motion.div>
      )}

      {/* Upload/extraction progress */}
      {(isUploading || isExtracting) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center justify-center gap-2 p-6 rounded-xl bg-slate-50 mb-4"
        >
          <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
          <span className="text-sm text-slate-600">
            {isUploading ? 'Beleg wird hochgeladen...' : 'Beleg wird analysiert...'}
          </span>
        </motion.div>
      )}

      {/* Add form or buttons */}
      {!extractedExpense && extractedExpenses.length === 0 && !isUploading && !isExtracting && (
        <>
          {showForm ? (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-3 p-4 rounded-xl bg-slate-50 mb-4"
            >
              <div className="grid grid-cols-2 gap-3">
                <select
                  value={formData.expense_type}
                  onChange={(e) =>
                    setFormData({ ...formData, expense_type: e.target.value as ExpenseType })
                  }
                  className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
                >
                  {expenseTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>

                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-200 text-sm"
                  />
                  <select
                    value={formData.currency}
                    onChange={(e) =>
                      setFormData({ ...formData, currency: e.target.value as Currency })
                    }
                    className="px-2 py-2 rounded-lg border border-slate-200 text-sm"
                  >
                    {currencies.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <input
                type="text"
                placeholder="Beschreibung (optional)"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
              />

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowForm(false)}
                  className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700"
                >
                  Abbrechen
                </button>
                <AnimatedButton
                  variant="primary"
                  size="sm"
                  onClick={handleSubmit}
                  disabled={!formData.amount || parseFloat(formData.amount) <= 0}
                  theme={theme}
                >
                  Hinzufügen
                </AnimatedButton>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex gap-3 mb-4"
            >
              {caseId && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 p-4 rounded-xl',
                    'border-2 border-dashed border-amber-200 text-amber-600',
                    'hover:border-amber-300 hover:text-amber-700 hover:bg-amber-50',
                    'transition-all'
                  )}
                >
                  <Upload className="w-5 h-5" />
                  <span className="font-medium">Beleg hochladen</span>
                </button>
              )}

              <button
                onClick={() => setShowForm(true)}
                className={cn(
                  'flex-1 flex items-center justify-center gap-2 p-4 rounded-xl',
                  'border-2 border-dashed border-slate-200 text-slate-500',
                  'hover:border-blue-300 hover:text-blue-500 hover:bg-blue-50',
                  'transition-all'
                )}
              >
                <Plus className="w-5 h-5" />
                <span className="font-medium">Manuell hinzufügen</span>
              </button>
            </motion.div>
          )}
        </>
      )}

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.25 }}
        className="flex items-center justify-between pt-4"
      >
        <button
          onClick={onSkip}
          className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
        >
          Überspringen - Keine Ausgaben
        </button>
        <AnimatedButton
          variant="primary"
          size="lg"
          onClick={onContinue}
          icon={<ArrowRight className="w-5 h-5" />}
          theme={theme}
        >
          Weiter
        </AnimatedButton>
      </motion.div>
    </motion.div>
  );
}
